import {AfterViewInit, Component, NgZone, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, Validators} from '@angular/forms';
import {HttpErrorResponse} from '@angular/common/http';
import {environment} from '../../../environments/environment';
import {
  AccessAreaId,
  CentralAuthService,
  CentralSession
} from '../../services/central-auth.service';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize(config: {client_id: string; callback: (response: {credential: string}) => void}): void;
          renderButton(element: HTMLElement, options: Record<string, unknown>): void;
        };
      };
    };
  }
}

type PortalIcon = 'globe' | 'person' | 'truck' | 'chart' | 'monitor' | 'snowflake' | 'trend' | 'brain';

interface ServiceLink {
  title: string;
  description: string;
  icon: PortalIcon;
  match: string[];
  exclude?: string[];
  organisationIds?: number[];
  roles: string[];
  url?: string;
  placeholder?: boolean;
}

interface AccessArea {
  id: AccessAreaId;
  title: string;
  description: string;
  icon: PortalIcon;
  services: ServiceLink[];
}

@Component({
  selector: 'app-access-portal',
  templateUrl: './access-portal.component.html',
  styleUrls: ['./access-portal.component.scss']
})
export class AccessPortalComponent implements OnInit, AfterViewInit, OnDestroy {
  readonly loginForm = this.fb.nonNullable.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(6)]]
  });
  readonly accessAreas: AccessArea[] = [
    {
      id: 'rwanda',
      title: 'Rwanda',
      description: 'Access Rwanda ONE Kasha Platform Services',
      icon: 'globe',
      services: [
        {
          title: 'Client Enrollment Form',
          description: 'Register and enroll clients',
          icon: 'person',
          match: ['rw pds enrollment form', 'rw pds client enrollment'],
          organisationIds: [106],
          roles: ['rw_pds_fulfillment', 'rw_pds_pharmacist', 'admin_eng'],
          url: environment.serviceUrls.rwandaEnrollment
        },
        {
          title: 'Delivery Form',
          description: 'Create and manage deliveries',
          icon: 'truck',
          match: ['rw pds delivery form'],
          exclude: ['dashboard'],
          organisationIds: [108, 39],
          roles: ['rw_pds_fulfillment', 'rw_pds_pharmacist', 'admin_eng'],
          url: environment.serviceUrls.rwandaDelivery
        },
        {
          title: 'Delivery Dashboard',
          description: 'View Rwanda delivery performance',
          icon: 'chart',
          match: ['rw pds delivery dashboard', 'rw pds dashboard'],
          organisationIds: [110],
          roles: ['rw_pds_business', 'rw_pds_customer_care', 'rw_pds_fulfillment', 'rw_pds_pharmacist', 'partner', 'business_partners', 'cxo_business_user', 'admin_eng'],
          url: environment.serviceUrls.rwandaDashboard
        },
        {
          title: 'Order Fulfillment Orchestration Service (OFOS)',
          description: 'Order fulfillment orchestration',
          icon: 'monitor',
          match: ['order fulfillment orchestration service', '(ofos)'],
          exclude: ['(local)', 'localhost', 'oms)', 'nigeria'],
          organisationIds: environment.production ? [] : [114],
          roles: ['rw_pds_customer_care', 'rw_pds_fulfillment', 'rw_pds_pharmacist', 'rw_pds_finance', 'admin_eng']
        },
        {
          title: 'Cold Chain Dashboard',
          description: 'Cold chain operations',
          icon: 'snowflake',
          match: ['cold chain dashboard'],
          exclude: ['demo', 'track & trace', 'sanofi', 'nigeria'],
          roles: ['rw_pds_fulfillment', 'rw_pds_pharmacist', 'rw_pds_business', 'admin_eng']
        }
      ]
    },
    {
      id: 'nigeria',
      title: 'Nigeria',
      description: 'Access Nigeria ONE Kasha Platform Services',
      icon: 'globe',
      services: [
        {
          title: 'Client Enrollment Form',
          description: 'Register and enroll clients',
          icon: 'person',
          match: ['ng pds enrollment form', 'ng pds client enrollment', 'nigeria enrollment'],
          roles: ['ng_pds_fulfillment', 'ng_pds_pharmacist', 'admin_eng'],
          placeholder: true
        },
        {
          title: 'Delivery Form',
          description: 'Create and manage deliveries',
          icon: 'truck',
          match: ['ng pds delivery form', 'nigeria delivery form'],
          exclude: ['dashboard'],
          roles: ['ng_pds_fulfillment', 'admin_eng'],
          placeholder: true
        },
        {
          title: 'Delivery Dashboard',
          description: 'View Nigeria delivery performance',
          icon: 'chart',
          match: ['ng pds delivery dashboard', 'ng pds dashboard', 'nigeria dashboard'],
          exclude: ['sc johnson', 'scjohnson'],
          roles: ['ng_pds_business', 'ng_pds_customer_care', 'ng_pds_fulfillment', 'ng_pds_pharmacist', 'partner', 'business_partners', 'cxo_business_user', 'admin_eng'],
          placeholder: true
        },
        {
          title: 'Order Fulfillment Orchestration Service (OFOS)',
          description: 'Order fulfillment orchestration',
          icon: 'monitor',
          match: ['nigeria ofos', 'ng pds ofos', 'nigeria order fulfillment'],
          roles: ['ng_pds_customer_care', 'ng_pds_fulfillment', 'ng_pds_pharmacist', 'ng_pds_finance', 'admin_eng'],
          placeholder: true
        },
        {
          title: 'Cold Chain Dashboard',
          description: 'Cold chain operations',
          icon: 'snowflake',
          match: ['nigeria cold chain', 'ng pds cold chain'],
          exclude: ['demo', 'track & trace', 'sanofi'],
          roles: ['ng_pds_fulfillment', 'ng_pds_pharmacist', 'ng_pds_business', 'admin_eng'],
          placeholder: true
        }
      ]
    },
    {
      id: 'insights',
      title: 'Partner insights',
      description: 'Additional dashboards for business partners',
      icon: 'trend',
      services: [
        {
          title: 'SC Johnson Delivery Dashboard',
          description: 'View Rwanda and Nigeria delivery performance',
          icon: 'chart',
          // Staging CMS: 116 "(STAGING) SC Johnson KPI Dashboard"
          // Production CMS: same org name without "(STAGING)"
          match: ['sc johnson kpi dashboard'],
          exclude: ['(local)', 'localhost', 'nigeria dashboard', 'ng pds dashboard'],
          organisationIds: environment.production ? [] : [116],
          roles: [
            'sc_johnson',
            'cxo_business_user',
            'rw_pds_pharmacist',
            'ng_pds_pharmacist',
            'rw_pds_business',
            'ng_pds_business',
            'rw_pds_fulfillment',
            'ng_pds_fulfillment',
            'admin_eng'
          ]
        },
        {
          title: 'Unified Transactions & Insights',
          description: 'Placeholder — destination will be added later',
          icon: 'monitor',
          match: ['unified transaction insights', 'unified transactions', 'utis'],
          roles: ['cxo_business_user', 'admin_eng'],
          placeholder: true
        },
        {
          title: 'One Kasha Trend Analysis',
          description: 'Placeholder — destination will be added later',
          icon: 'trend',
          match: ['one kasha trend analysis', 'trend analysis'],
          roles: ['ng_pds_business', 'rw_pds_business', 'cxo_business_user', 'admin_eng'],
          placeholder: true
        },
        {
          title: 'One Kasha Delivery Value Tracker',
          description: 'Placeholder — destination will be added later',
          icon: 'chart',
          match: ['one kasha delivery value tracker', 'rwanda delivery value tracker', 'delivery value tracker'],
          roles: ['ng_pds_business', 'rw_pds_business', 'cxo_business_user', 'admin_eng'],
          placeholder: true
        }
      ]
    }
  ];
  session: CentralSession | null = null;
  loading = true;
  submitting = false;
  emailModalOpen = false;
  passwordVisible = false;
  errorMessage = '';
  openingService = '';
  private watchdog?: ReturnType<typeof setInterval>;

  constructor(
    private fb: FormBuilder,
    private auth: CentralAuthService,
    private zone: NgZone
  ) {}

  get visibleAccessAreas(): AccessArea[] {
    const granted = new Set<AccessAreaId>(this.session?.accessAreas || []);
    return this.accessAreas.filter(area =>
      granted.has(area.id) && this.visibleServices(area).length > 0
    );
  }

  get grantedCountryLabel(): string {
    const granted = new Set<AccessAreaId>(this.session?.accessAreas || []);
    const names = this.accessAreas
      .filter(area => area.id !== 'insights' && granted.has(area.id))
      .map(area => this.areaTitle(area));
    if (this.hasScJohnsonAccess() && !granted.has('rwanda') && !granted.has('nigeria')) {
      return 'Nigeria and Rwanda';
    }
    if (!names.length) return '';
    if (names.length === 1) return names[0];
    return names.join(' and ');
  }

  hasScJohnsonAccess(): boolean {
    const roles = this.session?.roles || [];
    const names = this.session?.organisationNames || [];
    return roles.some(role => ['sc_johnson', 'scjohnson'].includes(role))
      || names.some(name => /sc\s*johnson/i.test(name));
  }

  areaTitle(area: AccessArea): string {
    return area.title;
  }

  areaDescription(area: AccessArea): string {
    return area.description;
  }

  async ngOnInit(): Promise<void> {
    this.session = await this.auth.restoreSession();
    if (this.session) await this.loadEntitlements();
    this.loading = false;
    if (!this.session) setTimeout(() => this.renderGoogleButton());
    this.watchdog = setInterval(() => this.validateSession(), environment.checkTokenInterval);
  }

  ngAfterViewInit(): void {
    this.loadGoogleIdentity();
  }

  ngOnDestroy(): void {
    if (this.watchdog) clearInterval(this.watchdog);
  }

  async signIn(): Promise<void> {
    this.errorMessage = '';
    if (this.loginForm.invalid) {
      this.loginForm.markAllAsTouched();
      this.errorMessage = 'Enter a valid email and a password of at least 6 characters.';
      return;
    }

    this.submitting = true;
    this.loading = true;
    try {
      const credentials = this.loginForm.getRawValue();
      this.session = await this.auth.emailLogin(credentials.email.trim(), credentials.password);
      await this.loadEntitlements();
      this.emailModalOpen = false;
      this.loginForm.reset();
    } catch (error) {
      this.errorMessage = this.describeError(error, 'We could not sign you in. Check your credentials and try again.');
    } finally {
      this.submitting = false;
      this.loading = false;
    }
  }

  logout(): void {
    this.auth.logout();
    this.session = null;
    this.errorMessage = '';
    setTimeout(() => this.renderGoogleButton());
  }

  serviceAvailable(service: ServiceLink): boolean {
    if (service.placeholder) return true;
    return !!this.auth.findOrganisation(service.match, service.exclude, service.organisationIds);
  }

  visibleServices(area: AccessArea): ServiceLink[] {
    const roles = this.session?.roles || [];
    const productionSupport = roles.some(role =>
      role === 'admin_eng' || role === 'administrator' || role === 'super_admin'
    );
    return area.services.filter(service =>
      (productionSupport || service.roles.some(role => roles.includes(role))) &&
      this.serviceAvailable(service)
    );
  }

  async accessService(service: ServiceLink): Promise<void> {
    if (service.placeholder) {
      this.errorMessage = `${service.title} is a placeholder and is not available yet.`;
      return;
    }
    const organisation = this.auth.findOrganisation(
      service.match, service.exclude, service.organisationIds
    );
    if (!organisation) {
      this.errorMessage = `${service.title} is not configured for your account. Contact your administrator.`;
      return;
    }
    this.openingService = service.title;
    this.errorMessage = '';
    try {
      await this.auth.openOrganisation(organisation, service.url);
    } catch (error) {
      if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
        this.auth.logout();
        this.session = null;
        this.errorMessage = 'Your session has expired. Please sign in again.';
        setTimeout(() => this.renderGoogleButton());
      } else {
        this.errorMessage = this.describeError(error, `Unable to open ${service.title}.`);
      }
    } finally {
      this.openingService = '';
    }
  }

  openEmailLogin(): void {
    this.errorMessage = '';
    this.emailModalOpen = true;
    setTimeout(() => document.getElementById('email')?.focus());
  }

  closeEmailLogin(): void {
    if (this.submitting) return;
    this.emailModalOpen = false;
    this.errorMessage = '';
    this.loginForm.reset();
  }

  trackArea(_: number, area: AccessArea): string {
    return area.id;
  }

  trackService(_: number, service: ServiceLink): string {
    return service.title;
  }

  private async loadEntitlements(): Promise<void> {
    try {
      this.session = await this.auth.refreshEntitlements();
    } catch (error) {
      if (error instanceof HttpErrorResponse && (error.status === 401 || error.status === 403)) {
        this.auth.logout();
        this.session = null;
        this.errorMessage = 'Your session has expired. Please sign in again.';
        return;
      }
      this.errorMessage = 'Your access was loaded from your profile. Some recent access changes may not appear yet.';
    }
  }

  private async validateSession(): Promise<void> {
    if (!this.session) return;
    const restored = await this.auth.restoreSession();
    this.zone.run(() => {
      if (restored) {
        this.session = restored;
      } else {
        this.session = null;
        this.errorMessage = 'Your session has expired. Please sign in again.';
        setTimeout(() => this.renderGoogleButton());
      }
    });
  }

  private loadGoogleIdentity(): void {
    if (window.google) {
      this.renderGoogleButton();
      return;
    }
    const existing = document.querySelector<HTMLScriptElement>('script[data-google-identity]');
    if (existing) {
      existing.addEventListener('load', () => this.renderGoogleButton());
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.dataset['googleIdentity'] = 'true';
    script.addEventListener('load', () => this.renderGoogleButton());
    document.head.appendChild(script);
  }

  private renderGoogleButton(): void {
    const element = document.getElementById('google-signin');
    if (!element || !window.google || this.session) return;
    element.innerHTML = '';
    window.google.accounts.id.initialize({
      client_id: environment.googleClientId,
      callback: response => this.zone.run(async () => {
        this.submitting = true;
        this.loading = true;
        this.errorMessage = '';
        try {
          this.session = await this.auth.googleLogin(response.credential);
          await this.loadEntitlements();
        } catch (error) {
          this.errorMessage = this.describeError(error, 'Google sign-in failed. Please try again.');
        } finally {
          this.submitting = false;
          this.loading = false;
        }
      })
    });
    window.google.accounts.id.renderButton(element, {
      type: 'standard',
      shape: 'pill',
      theme: 'outline',
      text: 'signin_with',
      size: 'medium',
      logo_alignment: 'left',
      width: 280
    });
  }

  private describeError(error: unknown, fallback: string): string {
    if (error instanceof HttpErrorResponse) {
      return error.error?.message || (error.status === 0
        ? 'The authentication service is currently unreachable.'
        : fallback);
    }
    return error instanceof Error ? error.message : fallback;
  }
}
